import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowBack, Email, LockReset, Phone } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import PhoneForgotPassword from "../../components/frontend/auth/PhoneForgotPassword";
import {
  forgotPassword,
  phoneForgotPassword,
  phoneResetPassword,
  verifyPhoneOtp,
} from "../../services/authService";
import { formatApiError } from "../../utils/formatApiError";
import {
  ForgotPasswordFormData,
  getForgotPasswordSchema,
} from "../../validation/authSchema";

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [method, setMethod] = useState<"email" | "phone">(() =>
    searchParams.get("method") === "email" ? "email" : "phone"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(getForgotPasswordSchema()),
  });

  const email = watch("email");

  const onEmailSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await forgotPassword(data.email);
      if (response.isSuccess) {
        setIsEmailSent(true);
      } else {
        setError(formatApiError(response));
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPhoneOtp = async (phone: string, _countryCode: string) => {
    const result = await phoneForgotPassword({ phoneNumber: phone });
    if (!result.isSuccess) throw new Error(result.message || "SMS_SEND_FAILED");
  };

  const handleVerifyOtp = async (
    phone: string,
    otp: string,
    countryCode: string,
  ) => {
    const result = await verifyPhoneOtp({
      phoneNumber: phone,
      otp,
      countryCode,
    });
    if (!result.isSuccess) throw new Error(result.message || "OTP_INVALID");
  };

  const handleResetPassword = async (data: {
    phoneNumber: string;
    newPassword: string;
  }) => {
    const result = await phoneResetPassword(data);
    if (!result.isSuccess) throw new Error(result.message || "RESET_FAILED");
    navigate("/login");
  };

  if (isEmailSent) {
    return (
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 440,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "success.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Email sx={{ color: "success.dark", fontSize: 28 }} />
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            className="notranslate"
            translate="no"
          >
            {t("auth.checkYourEmail")}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
            className="notranslate"
            translate="no"
          >
            {t("auth.passwordResetLinkSent")}
          </Typography>

          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ mb: 1, color: "primary.main" }}
          >
            {email}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 3 }}
            className="notranslate"
            translate="no"
          >
            {t("auth.linkExpireInfo")}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate("/login")}
            startIcon={<ArrowBack />}
            sx={{ mb: 1.5, fontWeight: 600 }}
            className="notranslate"
            translate="no"
          >
            {t("auth.backToLogin")}
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={() => {
              setIsEmailSent(false);
              onEmailSubmit({ email });
            }}
            disabled={isLoading}
            className="notranslate"
            translate="no"
          >
            {isLoading ? <CircularProgress size={18} /> : t("auth.resendEmail")}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 440,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "primary.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <LockReset sx={{ color: "primary.main", fontSize: 28 }} />
          </Box>
          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            className="notranslate"
            translate="no"
          >
            {t("auth.forgotPasswordTitle")}
          </Typography>
        </Box>

        {/* Email / Phone Toggle */}
        <ToggleButtonGroup
          value={method}
          exclusive
          onChange={(_, v) => v && setMethod(v)}
          fullWidth
          size="small"
          sx={{ mb: 3 }}
        >
          <ToggleButton value="phone">
            <Phone sx={{ mr: 1, fontSize: 18 }} />
            {t("auth.usePhone")}
          </ToggleButton>
          <ToggleButton value="email">
            <Email sx={{ mr: 1, fontSize: 18 }} />
            {t("auth.useEmail")}
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Email Form */}
        {method === "email" && (
          <Box
            component="form"
            onSubmit={handleSubmit(onEmailSubmit)}
            noValidate
          >
            <TextField
              {...register("email")}
              label={t("auth.emailAddress")}
              className="notranslate"
              translate="no"
              type="email"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={isLoading}
              autoComplete="email"
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading}
              sx={{ fontWeight: 600, py: 1.2, mb: 1.5 }}
              className="notranslate"
              translate="no"
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("auth.sendResetLink")
              )}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate("/login")}
              startIcon={<ArrowBack />}
              disabled={isLoading}
              className="notranslate"
              translate="no"
            >
              {t("auth.backToLogin")}
            </Button>
          </Box>
        )}

        {/* Phone Form */}
        {method === "phone" && (
          <PhoneForgotPassword
            onSendOtp={handleSendPhoneOtp}
            onVerifyOtp={handleVerifyOtp}
            onResetPassword={handleResetPassword}
            onBack={() => navigate("/login")}
          />
        )}

        <Divider sx={{ my: 3 }} />

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          className="notranslate"
          translate="no"
        >
          {t("auth.rememberPassword")}{" "}
          <Button
            variant="text"
            size="small"
            onClick={() => navigate("/login")}
            sx={{
              textTransform: "none",
              p: 0,
              minWidth: 0,
              verticalAlign: "baseline",
            }}
            className="notranslate"
            translate="no"
          >
            {t("auth.signInHere")}
          </Button>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
